import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { OverlayTrigger, Tooltip as BSTooltip } from 'react-bootstrap';
import DateField from 'src/components/date-field';
import {
  FINAL_SHIPMENT_STATUSES,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_LABELS,
  SHIPPING_TO_SHIPMENT_STATUS_MAP,
} from 'src/utils/constants';

const ShipmentDetailsForm = ({ shipment, run, onSubmit }) => {
  const isShipmentFinalStatus = useMemo(() => {
    return shipment?.status && FINAL_SHIPMENT_STATUSES.includes(shipment.status);
  }, [shipment]);

  const shipmentStatus = useMemo(() => {
    if (isShipmentFinalStatus) {
      return shipment.status;
    }

    return SHIPPING_TO_SHIPMENT_STATUS_MAP[run?.status] || shipment.status;
  }, [isShipmentFinalStatus, shipment, run]);

  return (
    <Formik
      initialValues={{
        name: shipment.name,
        estimatedShipDate: Date.parse(shipment.estimated_shipment_date),
        estimatedReceiveDate: Date.parse(shipment.estimated_delivery_date),
        actualShipDate: Date.parse(shipment.actual_shipment_date),
        actualReceiveDate: Date.parse(shipment.actual_delivery_date),
        trackingNumber: shipment.tracking_number,
        status: shipmentStatus,
        cost: shipment.actual_cost,
      }}
      onSubmit={form => onSubmit(form)}
    >
      {({ isSubmitting, handleChange }) => {
        return (
          <Form>
            <label className='text-left d-block' htmlFor='name'>
              Name: *
            </label>
            <Field required className='form-control mb-4' id='name' name='name' as='Input' />
            <label className='text-left d-block' htmlFor='status'>
              status: *
            </label>
            <div className='d-flex align-items-center mb-4'>
              <Field
                required
                component='select'
                id='status'
                name='status'
                className='form-control'
                disabled={!isShipmentFinalStatus}
                onChange={e => {
                  handleChange(e);
                }}
              >
                {Object.entries(SHIPMENT_STATUS_LABELS)
                  .filter(([key]) => {
                    if (!isShipmentFinalStatus) {
                      return true;
                    }

                    return ![SHIPMENT_STATUS.new, SHIPMENT_STATUS.queued].includes(key);
                  })
                  .map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
              </Field>
              {!isShipmentFinalStatus && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <BSTooltip>
                      <p className='mb-0'>
                        Status is currently disabled as it is controlled by the shipping run. It
                        will be enabled once the shipment is in transit.
                      </p>
                    </BSTooltip>
                  }
                >
                  <span className='spacer-left'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
            <Row>
              <Col xs='12' sm='6'>
                <label className='text-left d-block' htmlFor='estimatedShipDate'>
                  Estimated Ship Date:
                </label>
                <DateField disabled isRequiredAfterValue name='estimatedShipDate' />
              </Col>
              <Col xs='12' sm='6'>
                <label className='text-left d-block' htmlFor='estimatedReceiveDate'>
                  Estimated Receive Date:
                </label>
                <DateField disabled isRequiredAfterValue name='estimatedReceiveDate' />
              </Col>
            </Row>
            <Row>
              <Col xs='12' sm='6'>
                <label className='text-left d-block' htmlFor='actualShipDate'>
                  Actual Ship Date:
                </label>
                <DateField isRequiredAfterValue name='actualShipDate' />
              </Col>
              <Col xs='12' sm='6'>
                <label className='text-left d-block' htmlFor='actualReceiveDate'>
                  Actual Receive Date:
                </label>
                <DateField isRequiredAfterValue name='actualReceiveDate' />
              </Col>
            </Row>
            <label className='text-left d-block' htmlFor='cost'>
              Cost:
            </label>
            <Field
              className='form-control mb-4'
              id='cost'
              name='cost'
              type='number'
              min='0'
              step='0.01'
              as='Input'
            />
            <label className='text-left d-block' htmlFor='trackingNumber'>
              Shipping/Tracking #:
            </label>
            <Field
              className='form-control mb-4'
              id='trackingNumber'
              name='trackingNumber'
              as='Input'
            />
            <button
              type='submit'
              className='btn btn-lg btn-primary btn-block'
              disabled={isSubmitting}
            >
              Save
            </button>
          </Form>
        );
      }}
    </Formik>
  );
};

ShipmentDetailsForm.propTypes = {
  shipment: PropTypes.object.isRequired,
  run: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ShipmentDetailsForm;
